import type {
    GeoData,
    EnrichedGeoData,
    RegionalDomains,
    MainDomainToRegionalShares,
    EnrichedMarketShareData,
} from "../types/GeoDataTypes";

const createRegionalToMainDomainMap = (domains: RegionalDomains): Map<string, string> => {
    const map = new Map<string, string>();
    for (const mainDomain in domains) {
        map.set(mainDomain, mainDomain);
        domains[mainDomain].forEach((regionalDomain) => map.set(regionalDomain, mainDomain));
    }
    return map;
};

const calculateOverallDomainShare = (
    data: GeoData | EnrichedGeoData,
    regionalToMainDomainMap: Map<string, string>
): MainDomainToRegionalShares => {
    const mainDomainToRegionalShares: MainDomainToRegionalShares = {};
    const overallDomainShare: Record<string, number> = {};

    data.Data.forEach((countryData) => {
        const { Share, ShareList } = countryData;
        Object.entries(ShareList).forEach(([domain, domainShare]) => {
            const weightedShare = domainShare * Share;
            overallDomainShare[domain] = (overallDomainShare[domain] || 0) + weightedShare;
        });
    });

    Object.entries(overallDomainShare).forEach(([domain, totalShare]) => {
        const mainDomain = regionalToMainDomainMap.get(domain) || domain;
        if (!mainDomainToRegionalShares[mainDomain]) {
            mainDomainToRegionalShares[mainDomain] = [];
        }
        mainDomainToRegionalShares[mainDomain].push({ domain, totalShare });
    });

    Object.keys(mainDomainToRegionalShares).forEach((mainDomain) => {
        mainDomainToRegionalShares[mainDomain].sort((a, b) => b.totalShare - a.totalShare);
    });

    return mainDomainToRegionalShares;
};

const enrichData = (
    data: GeoData | EnrichedGeoData,
    regionalToMainDomainMap: Map<string, string>
): GeoData | EnrichedGeoData => {
    for (const entry of data.Data as EnrichedMarketShareData[]) {
        const EnrichedShareList: Record<string, { domains: string[]; aggregatedShare: number }> = {};
        const EnrichedUsersShareList: Record<string, { domains: string[]; aggregatedShare: number }> = {};
        for (const [domain, share] of Object.entries(entry.ShareList)) {
            const mainDomain = regionalToMainDomainMap.get(domain);
            if (mainDomain) {
                if (!EnrichedShareList[mainDomain]) {
                    EnrichedShareList[mainDomain] = { aggregatedShare: 0, domains: [] };
                }
                EnrichedShareList[mainDomain].aggregatedShare += share;
                EnrichedShareList[mainDomain].domains.push(domain);
            }
        }
        entry.EnrichedShareList = EnrichedShareList;
        if (entry?.UsersShareList && typeof entry.UsersShareList === 'object') {
            for (const [domain, share] of Object.entries(entry.UsersShareList)) {
                const mainDomain = regionalToMainDomainMap.get(domain);
                if (mainDomain) {
                    if (!EnrichedUsersShareList[mainDomain]) {
                        EnrichedUsersShareList[mainDomain] = { aggregatedShare: 0, domains: [] };
                    }
                    EnrichedUsersShareList[mainDomain].aggregatedShare += share;
                    EnrichedUsersShareList[mainDomain].domains.push(domain);
                }
            }
            entry.EnrichedUsersShareList = EnrichedUsersShareList;
        }
    }
    return data;
};

export const transformRegionalData = (data: GeoData | EnrichedGeoData): GeoData | EnrichedGeoData => {
    const regionalToMainDomainMap = createRegionalToMainDomainMap(data.RegionalDomains);
    const enrichedData = enrichData(data, regionalToMainDomainMap);
    return {
        ...enrichedData,
        OverallDomainShare: calculateOverallDomainShare(data, regionalToMainDomainMap),
    };
};
